import type { WorkflowBlock, BlockGroup } from "@/types/workflow";
import type { PlanLimits } from "./planTiers";

const DEFAULT_MIN_CANVAS_WIDTH = 4000;
const DEFAULT_MIN_CANVAS_HEIGHT = 2500;
const CANVAS_MARGIN = 1000;

const BLOCK_HALF_W = 100;
const BLOCK_HALF_H = 40;
// Collapsed-layer-card är 300 × ≥110 (50 % bredare än ett vanligt
// block, växer på höjden med antal chips). Halvorna används bara för
// att estimera world-bounds när dynamic-canvas växer — exakt höjd-
// tracking sker via blockDimensions per-id.
const CARD_HALF_W = 150;
const CARD_HALF_H = 80;

export interface CanvasBounds {
  /** Total canvas-bredd (CSS-px innan transform). */
  width: number;
  /** Total canvas-höjd (CSS-px innan transform). */
  height: number;
  /** Var world-koordinat (0,0) sitter inom canvasen, mätt från
   *  canvasens visuella top-left. Detta används för att positionera
   *  block-containern + räkna scroll-mål.
   *
   *  Tidigare hard-codade vi `width/2` / `height/2` (symmetrisk canvas
   *  runt origo). Det skapade en feedback-loop när user drog ett block
   *  åt höger: canvasen växte SYMMETRISKT (även åt vänster), origin
   *  hoppade i CSS, vyn upplevde "scroll åt fel håll". Nu ligger
   *  origin stilla även när bara en sida växer. */
  originX: number;
  originY: number;
}

/**
 * Räkna fram canvas-bbox + origin för dagens blocks/groups.
 *
 * Två lägen:
 *   - **Fixed canvas** (Free-tier, multiplier=1): returnerar exakt
 *     `maxWidth × maxHeight` med origin centrerat. Kan inte växa.
 *   - **Asymmetric growth** (Advanced/Pro/Max/Enterprise): bbox utökas
 *     bara på den sida där content faktiskt finns. `originX = -left`
 *     så world (0,0) bakas in på sin verkliga visuella plats —
 *     constant när bara höger sida växer (osv).
 *
 * Tier-clamp:en (om `planLimits.canvasWidth/Height` > 0) klipper
 * total-bredden men centerar inte runt origin när content kräver mer
 * än vad som ryms — overflow-content rules:as bort av drag-resolvern.
 */
export function computeDynamicCanvasSize(
  blocks: WorkflowBlock[],
  groups: BlockGroup[],
  planLimits?: PlanLimits | null,
): CanvasBounds {
  const maxWidth = planLimits && planLimits.canvasWidth > 0 ? planLimits.canvasWidth : -1;
  const maxHeight = planLimits && planLimits.canvasHeight > 0 ? planLimits.canvasHeight : -1;

  // Free-tier (canvasMultiplier === 1): canvas är fixed-size, origin
  // i mitten. Asymmetric growth gäller inte här.
  if (maxWidth > 0 && maxHeight > 0 && planLimits && planLimits.canvasMultiplier >= 1 && planLimits.canvasMultiplier <= 1) {
    return {
      width: maxWidth,
      height: maxHeight,
      originX: maxWidth / 2,
      originY: maxHeight / 2,
    };
  }

  const MIN_CANVAS_WIDTH = maxWidth > 0 ? Math.min(DEFAULT_MIN_CANVAS_WIDTH, maxWidth) : DEFAULT_MIN_CANVAS_WIDTH;
  const MIN_CANVAS_HEIGHT = maxHeight > 0 ? Math.min(DEFAULT_MIN_CANVAS_HEIGHT, maxHeight) : DEFAULT_MIN_CANVAS_HEIGHT;

  // World-bbox: minst MIN-storlek runt origo, växer asymmetriskt med
  // block/group-positions.
  let left = -MIN_CANVAS_WIDTH / 2;
  let right = MIN_CANVAS_WIDTH / 2;
  let top = -MIN_CANVAS_HEIGHT / 2;
  let bottom = MIN_CANVAS_HEIGHT / 2;

  for (const b of blocks) {
    if (b.x - BLOCK_HALF_W < left) left = b.x - BLOCK_HALF_W;
    if (b.x + BLOCK_HALF_W > right) right = b.x + BLOCK_HALF_W;
    if (b.y - BLOCK_HALF_H < top) top = b.y - BLOCK_HALF_H;
    if (b.y + BLOCK_HALF_H > bottom) bottom = b.y + BLOCK_HALF_H;
  }

  for (const g of groups) {
    if (g.collapsed && g.collapsedX != null && g.collapsedY != null) {
      if (g.collapsedX - CARD_HALF_W < left) left = g.collapsedX - CARD_HALF_W;
      if (g.collapsedX + CARD_HALF_W > right) right = g.collapsedX + CARD_HALF_W;
      if (g.collapsedY - CARD_HALF_H < top) top = g.collapsedY - CARD_HALF_H;
      if (g.collapsedY + CARD_HALF_H > bottom) bottom = g.collapsedY + CARD_HALF_H;
    }
  }

  // Lägg på margin på de sidor som faktiskt sticker utanför default-
  // bbox:en. Det undviker att canvasen växer i två riktningar för en
  // ensidig drag.
  if (left < -MIN_CANVAS_WIDTH / 2) left -= CANVAS_MARGIN;
  if (right > MIN_CANVAS_WIDTH / 2) right += CANVAS_MARGIN;
  if (top < -MIN_CANVAS_HEIGHT / 2) top -= CANVAS_MARGIN;
  if (bottom > MIN_CANVAS_HEIGHT / 2) bottom += CANVAS_MARGIN;

  // Inget extra snap här — blocks är redan grid-snappade till 20-px-
  // multipler (BLOCK_HALF_W/H = 100/40, alla multiplar av 20), så
  // bbox är stabil mellan frames. Tidigare 100-px-ceil/floor införde
  // bara accidentell utvidgning på MIN-bounds.

  let width = right - left;
  let height = bottom - top;
  let originX = -left;
  let originY = -top;

  // Tier-clamp: om bbox sträcker sig över vad tier tillåter, klipp
  // totalstorleken. Origin behåller sin asymmetriska plats för att
  // vyer inte ska hoppa när nytt content lägger sig vid clamp-gränsen.
  if (maxWidth > 0 && width > maxWidth) {
    width = maxWidth;
    originX = Math.max(0, Math.min(maxWidth, originX));
  }
  if (maxHeight > 0 && height > maxHeight) {
    height = maxHeight;
    originY = Math.max(0, Math.min(maxHeight, originY));
  }

  return { width, height, originX, originY };
}
